// A video small enough to send, made on the colleague's own computer or phone.
//
// Owner, 2026-10-22: 「video发不了？只能16mb而已？？？」. WhatsApp takes a clip of
// at most 16 MB, and the API refuses a bigger one for that reason
// (media_send.go) — after the whole file has gone up. The server cannot shrink
// one: each API copy has 256 MB of memory and a quarter of a CPU and uses
// 100–135 MB already, and ffmpeg needs about 130 MB more (Pod Monitor,
// 2026-10-22) — the shop and the dealer system would go down with it. So the
// browser does it, with its own encoder (WebCodecs, through mediabunny), before
// the upload: a phone's .mov comes out as an MP4 too.
//
// Everything of a clip fits in fitTarget: the sound is copied when it is AAC
// at a sane rate, else made AAC at 96 kbit/s, and the picture gets the rest,
// at the size that rate carries — measured on a 145 s company clip
// (2026-10-22): 720p from 1 Mbit/s looks unchanged, 540p from 0.55, 480p from
// 0.4, 360p below. A clip longer than maxSeconds would be a blur: the
// colleague is asked to cut it.

/** What the API takes for a clip (media_send.go maxVideoBytes). */
export const videoSendLimit = 16 * 1024 * 1024;

// fitTarget leaves room under WhatsApp's 16,000,000 bytes as well as the API's
// 16 MiB, and for an encoder that overshoots its rate a little.
const fitTarget = 15 * 1024 * 1024;
const maxSeconds = 5 * 60;
// Sound copied as it is up to this rate; above it, made AAC at soundRate.
const copySoundUpTo = 192_000;
const soundRate = 96_000;
const maxFrameRate = 30;

/** Why a clip could not be made small enough, in the colleague's words. */
export class VideoFitError extends Error {}

// videoName is a file name a clip goes by, for a file the system gave no type
// of its own: a .mov picked on some computers arrives as "" or as
// application/octet-stream, and the API takes a clip only as video/mp4.
const videoName = /\.(mov|mp4|m4v|3gp|3gpp|avi|mkv|webm|mpe?g|hevc)$/i;

/** isVideo says a picked file is a clip, by its type or else by its name. */
export function isVideo(f: File): boolean {
  if (f.type.startsWith("video/")) return true;
  return (f.type === "" || f.type === "application/octet-stream") && videoName.test(f.name);
}

/**
 * needsFit says a picked file is a clip the API would refuse or WhatsApp
 * could not play as it is: too big, or not an MP4.
 */
export function needsFit(f: File): boolean {
  return isVideo(f) && (f.size > fitTarget || f.type !== "video/mp4");
}

/** The short side a picture gets at a video bit rate. */
export function shortSideFor(videoBits: number): number {
  if (videoBits >= 1_000_000) return 720;
  if (videoBits >= 550_000) return 540;
  if (videoBits >= 400_000) return 480;
  return 360;
}

/** The size a picture of w×h gets with its short side at most short, even. */
export function fitSize(w: number, h: number, short: number): { width: number; height: number } {
  const scale = Math.min(1, short / Math.min(w, h));
  const even = (n: number) => Math.max(2, Math.round((n * scale) / 2) * 2);
  return { width: even(w), height: even(h) };
}

/** The picture's rate in bits a second when the sound takes soundBits a second. */
export function videoBitsFor(seconds: number, soundBits: number, target = fitTarget): number {
  return Math.max(200_000, Math.floor(((target * 8) / seconds - soundBits) * 0.92));
}

// maxVideoBits is as much as a 720p clip for a phone screen needs.
const maxVideoBits = 2_500_000;

/**
 * pictureCap is the most the picture of a clip whose own picture runs at
 * sourceBits a second is given: never more than it had — a 6.6 MB .mov made an
 * MP4 came out 14 MB when the rule filled the budget (2026-10-22) — nor more
 * than maxVideoBits.
 */
export function pictureCap(sourceBits: number | undefined): number {
  if (!sourceBits || !Number.isFinite(sourceBits) || sourceBits <= 0) return maxVideoBits;
  return Math.max(300_000, Math.min(maxVideoBits, Math.floor(sourceBits)));
}

/** The name the made clip goes by: the picked one's, as .mp4. */
export function mp4Name(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, "") || "video";
  return `${base}.mp4`;
}

/**
 * fitVideo is f made small enough to send: f itself when it already is,
 * else an MP4 under fitTarget. onProgress gets 0..1. A failure is a
 * VideoFitError saying what to do. signal cancels it.
 */
export async function fitVideo(f: File, onProgress?: (done: number) => void, signal?: AbortSignal): Promise<File> {
  if (!needsFit(f)) return f;
  if (typeof VideoEncoder === "undefined") {
    throw new VideoFitError("这个浏览器压不了影片：请用电脑的 Chrome 或 Edge、手机的 Chrome，或先把影片剪到 15MB 以下");
  }
  const mb = await import("mediabunny").catch(() => {
    throw new VideoFitError("压缩工具没载入：请刷新页面，再选一次影片");
  });
  const input = new mb.Input({ source: new mb.BlobSource(f), formats: mb.ALL_FORMATS });
  try {
    const video = await input.getPrimaryVideoTrack().catch(() => null);
    if (!video) throw new VideoFitError("读不出这支影片：换一支，或用手机的「分享」转成 MP4 再选");
    const seconds = await input.computeDuration();
    if (!(seconds > 0)) throw new VideoFitError("读不出这支影片有多长：换一支试试");
    if (seconds > maxSeconds) {
      throw new VideoFitError(`影片有 ${Math.ceil(seconds / 60)} 分钟，压到 16MB 会太糊：最多 5 分钟，请先剪短`);
    }
    const sound = await input.getPrimaryAudioTrack();
    let soundBits = 0;
    let copySound = false;
    if (sound) {
      const codec = await sound.getCodec();
      const stats = await sound.computePacketStats(200).catch(() => null);
      const rate = stats?.averageBitrate ?? Infinity;
      copySound = codec === "aac" && rate <= copySoundUpTo;
      soundBits = copySound ? rate : soundRate;
    }
    const w = await video.getDisplayWidth();
    const h = await video.getDisplayHeight();
    const frames = await video.computePacketStats(120).catch(() => null);
    const frameRate = frames && frames.averagePacketRate > maxFrameRate + 1 ? maxFrameRate : undefined;

    // A clip made only to be an MP4 is not made bigger than it was.
    let videoBits = Math.min(videoBitsFor(seconds, soundBits), pictureCap(frames?.averageBitrate));
    for (let attempt = 0; attempt < 2; attempt++) {
      const size = fitSize(w, h, shortSideFor(videoBits));
      const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: "in-memory" }), target: new mb.BufferTarget() });
      const conversion = await mb.Conversion.init({
        input,
        output,
        tracks: "primary",
        video: {
          codec: "avc",
          width: size.width,
          height: size.height,
          fit: "fill",
          frameRate,
          quality: new mb.Quality({ bitrate: videoBits }),
          forceTranscode: true,
        },
        audio: copySound ? {} : { codec: "aac", quality: new mb.Quality({ bitrate: soundRate }), forceTranscode: true },
      });
      if (!conversion.isValid) {
        throw new VideoFitError("这台电脑压不了这支影片：请用电脑的 Chrome 或 Edge，或先把影片剪到 15MB 以下");
      }
      const stop = () => void conversion.cancel();
      signal?.addEventListener("abort", stop, { once: true });
      conversion.onProgress = (done) => onProgress?.((attempt + Math.min(1, done)) / (attempt + 1));
      try {
        await conversion.execute();
      } finally {
        signal?.removeEventListener("abort", stop);
      }
      const buf = output.target.buffer;
      if (!buf) throw new VideoFitError("压缩没有成功：再选一次影片试试");
      if (buf.byteLength <= fitTarget) return new File([buf], mp4Name(f.name), { type: "video/mp4" });
      // Over: the encoder overshot. Once more, at the rate that would have fit.
      videoBits = Math.floor(videoBits * (fitTarget / buf.byteLength) * 0.9);
    }
    throw new VideoFitError("压了还是超过 15MB：请先把影片剪短");
  } finally {
    input.dispose();
  }
}
